import React, { useEffect, useRef, useState } from 'react';

import { RoleplayPersonaPortrait } from 'components/roleplay/RoleplayPersonaPortrait';
import type { RoleplayExpression } from 'services/api/roleplayClient';
import type { RoleplayScenario } from './types';

interface PersonaPortraitProps {
  persona: RoleplayScenario['persona'];
  expression?: RoleplayExpression;
  className?: string;
}

const PersonaPortraitComponent: React.FC<PersonaPortraitProps> = ({
  persona,
  expression = persona.starting_expression,
  className = '',
}) => {
  const [layers, setLayers] = useState<[RoleplayExpression, RoleplayExpression]>([
    expression,
    expression,
  ]);
  const [frontLayer, setFrontLayer] = useState<0 | 1>(0);
  const [transitioningFrom, setTransitioningFrom] = useState<RoleplayExpression | null>(null);
  const frontLayerRef = useRef<0 | 1>(0);
  const visibleExpressionRef = useRef(expression);

  useEffect(() => {
    if (expression === visibleExpressionRef.current) return undefined;
    const outgoingExpression = visibleExpressionRef.current;
    const nextLayer: 0 | 1 = frontLayerRef.current === 0 ? 1 : 0;
    setTransitioningFrom(outgoingExpression);
    setLayers((current) => {
      const next: [RoleplayExpression, RoleplayExpression] = [...current];
      next[nextLayer] = expression;
      return next;
    });

    // Paint the next crop into the hidden buffer before changing opacity. Two
    // frames prevent the browser from briefly exposing an unpainted image.
    const requestFrame = window.requestAnimationFrame?.bind(window)
      ?? ((callback: FrameRequestCallback) => window.setTimeout(() => callback(performance.now()), 16));
    const cancelFrame = window.cancelAnimationFrame?.bind(window)
      ?? ((handle: number) => window.clearTimeout(handle));
    let secondFrame = 0;
    const firstFrame = requestFrame(() => {
      secondFrame = requestFrame(() => {
        frontLayerRef.current = nextLayer;
        visibleExpressionRef.current = expression;
        setFrontLayer(nextLayer);
      });
    });
    const finishFade = window.setTimeout(() => setTransitioningFrom(null), 420);
    return () => {
      cancelFrame(firstFrame);
      if (secondFrame) cancelFrame(secondFrame);
      window.clearTimeout(finishFade);
    };
  }, [expression]);

  return (
    <div
      className={`rp-portrait-transition-surface relative overflow-hidden ${className}`}
      data-testid={`roleplay-portrait-transition-${persona.key}`}
      data-transitioning-from={transitioningFrom ?? undefined}
    >
      {layers.map((layerExpression, index) => {
        const isVisible = index === frontLayer;
        return (
        <RoleplayPersonaPortrait
          key={`portrait-buffer-${index}`}
          persona={persona}
          expression={layerExpression}
          ariaHidden={!isVisible}
          showTestId={isVisible}
          className={`rp-portrait-layer absolute inset-0 h-full w-full ${isVisible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        />
        );
      })}
    </div>
  );
};

export const PersonaPortrait = React.memo(PersonaPortraitComponent);
PersonaPortrait.displayName = 'PersonaPortrait';
